import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, collectionGroup, query, where, getDocs, limit } from 'firebase/firestore';
import { XCircleIcon, CheckCircleIcon, QrCodeIcon, DocumentArrowDownIcon } from '@heroicons/react/24/solid';
import { parseScanInput } from '../lib/scanCode';

const FileFinderView = ({ db }) => {
  const [status, setStatus] = useState('idle');
  const [inputValue, setInputValue] = useState('');
  const [foundSheet, setFoundSheet] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  // §9.3 Pilar 3 (corrige D4): mismo camino que /scan — parseScanInput entiende el
  // QR v1, TODOS los QR legados ya impresos ({jobId,sheetDocId} y {jobId,sheetId})
  // y el hri TECLEADO (interfaz humana: antes cualquier texto era "not valid JSON").
  const handleScanSubmit = async (event) => {
    event.preventDefault();
    if (!inputValue || !db) return;

    setIsLoading(true);
    setStatus('loading');
    setFoundSheet(null);

    try {
      const parsed = parseScanInput(inputValue);
      if (!parsed) throw new Error('Entrada vacía.');

      if (parsed.kind === 'piece') {
        throw new Error('Eso es una etiqueta de PIEZA — úsala en el Inventario de Piezas del job.');
      }
      if (parsed.kind === 'artifact' || parsed.kind === 'unknown') {
        throw new Error('Código no reconocido como plancha. Escanea el QR de la plancha o teclea su nombre (ej. 1116-COR_002P).');
      }

      let docSnap = null;
      if (parsed.kind === 'sheet') {
        // Lookup directo por ID (ultra-eficiente) — QR v1 o legado.
        docSnap = await getDoc(doc(db, 'jobs', parsed.jobId, 'sheets', parsed.id));
        if (!docSnap.exists()) {
          throw new Error(`La plancha del código no existe (job ${parsed.jobId}).`);
        }
      } else {
        // Texto tecleado: UNA collection-group query por hriSearch (minúsculas, sin '#').
        const term = parsed.hri;
        const snap = await getDocs(query(
          collectionGroup(db, 'sheets'),
          where('hriSearch', '>=', term),
          where('hriSearch', '<=', term + '\uf8ff'),
          limit(10)
        ));
        if (snap.empty) {
          throw new Error(`No hay planchas que coincidan con "${inputValue}".`);
        }
        // Preferir la coincidencia exacta; si no, la primera del prefijo.
        docSnap = snap.docs.find(d => d.data().hriSearch === term) || snap.docs[0];
      }

      setFoundSheet({ id: docSnap.id, ...docSnap.data() });
      setStatus('found');

    } catch (error) {
      console.error("Find File Error:", error);
      setErrorMessage(error.message);
      setStatus('error');
    } finally {
      setIsLoading(false);
      setInputValue('');
    }
  };

  const resetState = () => {
    setStatus('idle');
    setFoundSheet(null);
    setErrorMessage('');
    setInputValue('');
  };

  useEffect(() => {
    if (status === 'idle') {
      inputRef.current?.focus();
    }
  }, [status]);

  // --- UI RENDERING (Ajustado para mostrar el HRI) ---
  if (status === 'idle' || status === 'loading') {
    return (
      <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center p-4">
        <QrCodeIcon className="w-24 h-24 text-gray-400 mb-6" />
        <h1 className="text-3xl font-bold text-gray-800">Buscador de Archivos de Producción</h1>
        <p className="text-lg text-gray-500 mt-2">
          Escanee el código QR de la plancha con el lector, o teclee su nombre (ej. 1116-COR_002P)
        </p>
        <form onSubmit={handleScanSubmit} className="mt-8 w-full max-w-lg">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            className="w-full text-center bg-white border-2 border-gray-300 rounded-lg p-4 text-2xl focus:border-blue-500 focus:ring-blue-500"
            placeholder={isLoading ? "Buscando..." : "Escaneo QR o nombre de plancha..."}
            disabled={isLoading}
          />
        </form>
         {isLoading && (
            <div className="mt-6 flex items-center text-gray-600 text-lg">
                <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Buscando en la base de datos...
            </div>
        )}
      </div>
    );
  }

  if (status === 'found' && foundSheet) {
    // Usamos el campo 'hri' (Human-Readable ID) que debe estar en la base de datos
    const humanReadableId = foundSheet.hri || `${foundSheet.jobId}-${foundSheet.bloque}-${foundSheet.plancha}`;
    return (
        <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center p-4 animate-fade-in">
            <div className="w-full max-w-4xl p-8 bg-white rounded-xl shadow-lg">
                <div className="text-center mb-6">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-3xl font-bold">Plancha Encontrada</h2>
                    <p className="text-lg text-gray-500">{humanReadableId}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-xl font-semibold mb-4">Archivo de Impresión</h3>
                        <p className="text-gray-700 mb-4 truncate" title={foundSheet.printFile?.name || 'No asignado'}>{foundSheet.printFile?.name || 'No asignado'}</p>
                        <button 
                            disabled={!foundSheet.printFile?.url}
                            onClick={() => window.open(foundSheet.printFile.url, '_blank')}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold disabled:bg-gray-400 hover:bg-blue-700 transition-all">
                            <DocumentArrowDownIcon className="w-6 h-6"/>
                            Descargar Impresión
                        </button>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-xl font-semibold mb-4">Archivo de Corte</h3>
                        <p className="text-gray-700 mb-4 truncate" title={foundSheet.cutFile?.name || 'No asignado'}>{foundSheet.cutFile?.name || 'No asignado'}</p>
                         <button 
                            disabled={!foundSheet.cutFile?.url}
                            onClick={() => window.open(foundSheet.cutFile.url, '_blank')}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold disabled:bg-gray-400 hover:bg-blue-700 transition-all">
                            <DocumentArrowDownIcon className="w-6 h-6"/>
                            Descargar Corte
                        </button>
                    </div>
                </div>
                <div className="mt-8 text-center">
                    <button onClick={resetState} className="text-blue-600 hover:underline font-semibold">Realizar otra búsqueda</button>
                </div>
            </div>
        </div>
    );
  }

  if (status === 'error') {
      return (
        <div 
          className="w-full h-full bg-red-600 text-white flex flex-col items-center justify-center p-4 cursor-pointer"
          onClick={resetState}
        >
          <XCircleIcon className="w-24 h-24"/>
          <p className="text-4xl font-bold mt-4">Error</p>
          <p className="text-xl mt-2">{errorMessage}</p>
          <p className="text-lg mt-8">(Click para intentar de nuevo)</p>
        </div>
      );
  }

  return null;
};

export default FileFinderView;

